import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import toast from '../../components/ui/Toast';
import NavigationSidebar from '../../components/NavigationSidebar';
import TutorialAccessButton from '../../components/TutorialAccessButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import agentService from '../../services/agentService';
import ContentCard from './components/ContentCard';
import ContentPreviewModal from './components/ContentPreviewModal';
import ExportModal from './components/ExportModal';
import FilterPanel from './components/FilterPanel';
import AgentChatInterface from '../../components/AgentChatInterface';
import ErrorBoundary from '../../components/ErrorBoundary';


const MarketingAgentInterface = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, currentOrganizationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState('content'); // 'content' or 'chat'
  
  // Filter states
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [performanceRange, setPerformanceRange] = useState([0, 100]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const categories = [
    { id: 'all', label: 'All Content', icon: 'Layers', contentType: null },
    { id: 'ads', label: 'Ads', icon: 'TrendingUp', contentType: 'ad_copy' },
    { id: 'email', label: 'Email Templates', icon: 'Mail', contentType: 'email_template' },
    { id: 'videos', label: 'Videos', icon: 'Video', contentType: 'video_script' },
    { id: 'strategy', label: 'Strategy Documents', icon: 'FileText', contentType: 'report' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'retail', label: 'Retail' },
    { value: 'education', label: 'Education' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'manufacturing', label: 'Manufacturing' }
  ];

  useEffect(() => {
    if (user?.id) {
      loadContent();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [content, activeCategory, searchQuery, selectedIndustry, performanceRange, dateRange]);

  const loadContent = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await agentService?.getContentLibrary(user?.id, {
        agentId: 'marketing-ops',
        teamId: currentOrganizationId
      });
      
      if (error) throw error;
      setContent(data || []);
      
      // Load favorites from metadata
      const favs = (data || [])?.filter(item => item?.metadata?.is_favorite)?.map(item => item?.id);
      setFavorites(favs);
    } catch (error) {
      toast?.error('Failed to load content library');
      console.error('Load content error:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...content];

    // Category filter
    if (activeCategory !== 'all') {
      const category = categories?.find(cat => cat?.id === activeCategory);
      if (category?.contentType) {
        filtered = filtered?.filter(item => item?.content_type === category?.contentType);
      }
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(item => 
        item?.title?.toLowerCase()?.includes(query) ||
        item?.content?.toLowerCase()?.includes(query) ||
        item?.metadata?.category?.toLowerCase()?.includes(query)
      );
    }

    // Industry filter
    if (selectedIndustry) {
      filtered = filtered?.filter(item => item?.metadata?.industry === selectedIndustry);
    }

    // Performance score filter
    filtered = filtered?.filter(item => {
      const score = item?.metadata?.performance_score || 0;
      return score >= performanceRange?.[0] && score <= performanceRange?.[1];
    });

    // Date range filter
    if (dateRange?.start) {
      filtered = filtered?.filter(item => new Date(item?.created_at) >= new Date(dateRange?.start));
    }
    if (dateRange?.end) {
      filtered = filtered?.filter(item => new Date(item?.created_at) <= new Date(dateRange?.end));
    }

    setFilteredContent(filtered);
  };

  const handleToggleFavorite = async (contentId) => {
    try {
      const isFavorite = favorites?.includes(contentId);
      const updatedFavorites = isFavorite
        ? favorites?.filter(id => id !== contentId)
        : [...favorites, contentId];
      
      setFavorites(updatedFavorites);
      
      // Update in database (would need updateContent method in agentService)
      toast?.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      toast?.error('Failed to update favorites');
      console.error('Toggle favorite error:', error);
    }
  };

  const handleExport = (format) => {
    try {
      const exportData = filteredContent?.map(item => ({
        title: item?.title,
        type: item?.content_type,
        content: item?.content,
        industry: item?.metadata?.industry,
        performance_score: item?.metadata?.performance_score,
        source_url: item?.metadata?.source_url,
        created_at: item?.created_at
      }));

      if (format === 'json') {
        const dataStr = JSON.stringify(exportData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `marketing-content-${Date.now()}.json`;
        link?.click();
      } else if (format === 'csv') {
        const headers = ['Title', 'Type', 'Industry', 'Performance Score', 'Source URL', 'Created At'];
        const rows = exportData?.map(item => [
          item?.title,
          item?.type,
          item?.industry || 'N/A',
          item?.performance_score || 'N/A',
          item?.source_url || 'N/A',
          new Date(item?.created_at)?.toLocaleDateString()
        ]);
        const csvContent = [headers, ...rows]?.map(row => row?.join(','))?.join('\n');
        const dataBlob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `marketing-content-${Date.now()}.csv`;
        link?.click();
      }

      toast?.success(`Exported ${filteredContent?.length} items as ${format?.toUpperCase()}`);
      setShowExportModal(false);
    } catch (error) {
      toast?.error('Failed to export content');
      console.error('Export error:', error);
    }
  };

  const handleClearFilters = () => {
    setActiveCategory('all');
    setSearchQuery('');
    setSelectedIndustry('');
    setPerformanceRange([0, 100]);
    setDateRange({ start: '', end: '' });
  };

  if (loading) {
    return (
      <ErrorBoundary>
        <div className="flex h-screen bg-neutral-50">
          <NavigationSidebar />
          <div className="flex-1 flex items-center justify-center ml-0 lg:ml-60">
            <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <Toaster />
        <NavigationSidebar />
        <div className="lg:ml-64 min-h-screen">
          <div className="relative overflow-hidden">
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
            
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {/* Header with Glass Effect */}
              <div className="glass-strong rounded-3xl p-8 mb-8 shadow-glass-lg backdrop-blur-xl">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-glass p-2">
                      <img 
                        src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                        alt="DraftIQ Logo" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-foreground font-heading">Marketing Agent</h1>
                      <p className="text-muted-foreground mt-1">AI-powered content creation and campaign management</p>
                    </div>
                  </div>
                  <TutorialAccessButton onTutorialStart={() => console.log('Tutorial started')} />
                </div>

                {/* Tab Switcher with Modern Design */}
                <div className="flex gap-2 p-1.5 glass-subtle rounded-2xl inline-flex">
                  <button
                    onClick={() => setActiveTab('content')}
                    className={`
                      px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2
                      ${
                        activeTab === 'content' ?'bg-primary text-primary-foreground shadow-lg' :'text-muted-foreground hover:text-foreground hover:bg-background/50'
                      }
                    `}
                  >
                    <Icon name="Layers" size={18} />
                    Content Library
                  </button>
                  <button
                    onClick={() => setActiveTab('chat')}
                    className={`
                      px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2
                      ${
                        activeTab === 'chat' ?'bg-primary text-primary-foreground shadow-lg' :'text-muted-foreground hover:text-foreground hover:bg-background/50'
                      }
                    `}
                  >
                    <Icon name="MessageSquare" size={18} />
                    Chat
                  </button>
                </div>
              </div>

              {activeTab === 'chat' ? (
                <div className="h-[calc(100vh-280px)]">
                  <AgentChatInterface
                    userId={user?.id}
                    agentId="marketing-ops"
                    agentName="Marketing Agent"
                    agentIcon="TrendingUp"
                    agentDescription="Content creation and campaign management specialist"
                  />
                </div>
              ) : (
                <>
                  {/* Category Tabs with Glass Effect */}
                  <div className="glass-strong rounded-2xl p-2 mb-8 shadow-glass inline-flex gap-2">
                    {categories?.map((category) => (
                      <button
                        key={category?.id}
                        onClick={() => setActiveCategory(category?.id)}
                        className={`
                          flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300
                          ${
                            activeCategory === category?.id
                              ? 'bg-primary text-primary-foreground shadow-lg'
                              : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                          }
                        `}
                      >
                        <Icon name={category?.icon} size={18} />
                        <span className="hidden sm:inline">{category?.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Filter Panel */}
                  <FilterPanel
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    selectedIndustry={selectedIndustry}
                    setSelectedIndustry={setSelectedIndustry}
                    industries={industries}
                    performanceRange={performanceRange}
                    setPerformanceRange={setPerformanceRange}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    onClearFilters={handleClearFilters}
                  />

                  {/* Content Grid */}
                  {loading ? (
                    <div className="flex justify-center items-center py-20">
                      <div className="glass-strong rounded-2xl p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                      </div>
                    </div>
                  ) : filteredContent?.length === 0 ? (
                    <div className="glass-strong rounded-3xl p-12 text-center shadow-glass">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-muted/50 to-muted/30 flex items-center justify-center mx-auto mb-6">
                        <Icon name="Inbox" size={40} className="text-muted-foreground" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-2 font-heading">No content found</h3>
                      <p className="text-muted-foreground mb-6">Try adjusting your filters or create new content</p>
                      <Button variant="default" iconName="Plus" className="shadow-lg">
                        Create Content
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredContent?.map((item) => (
                        <ContentCard
                          key={item?.id}
                          content={item}
                          viewMode={viewMode}
                          onView={() => setSelectedContent(item)}
                          onPreview={() => setSelectedContent(item)}
                          onToggleFavorite={() => handleToggleFavorite(item?.id)}
                          isFavorite={favorites?.includes(item?.id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {selectedContent && (
          <ContentPreviewModal
            content={selectedContent}
            isFavorite={favorites?.includes(selectedContent?.id)}
            onClose={() => setSelectedContent(null)}
            onToggleFavorite={() => handleToggleFavorite(selectedContent?.id)}
            onExport={() => {
              setShowExportModal(true);
              setSelectedContent(null);
            }}
          />
        )}

        {showExportModal && (
          <ExportModal
            content={selectedContent}
            itemCount={filteredContent?.length}
            onClose={() => setShowExportModal(false)}
            onExport={handleExport}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default MarketingAgentInterface;